/**
 * Utilities for JWT token handling
 */

/**
 * Validates token format and structure
 * @param token JWT token to validate
 * @returns boolean indicating whether token is valid
 */
export const isValidTokenFormat = (token: string | null): boolean => {
  if (!token) return false;
  
  // Check basic token structure (header.payload.signature)
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  try {
    // Try to decode the header and payload
    JSON.parse(atob(parts[0]));
    JSON.parse(atob(parts[1]));
    return true;
  } catch (e) {
    console.error('Error validating token:', e);
    return false;
  }
};

/**
 * Decodes the token payload
 * @param token JWT token to decode
 * @returns Decoded payload or null if invalid
 */
export const decodeToken = (token: string | null): any | null => {
  if (!token || !isValidTokenFormat(token)) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch (e) {
    console.error('Error decoding token:', e);
    return null;
  }
};

/**
 * Checks if token is expired
 * @param token JWT token to check
 * @returns boolean indicating whether token is expired
 */
export const isTokenExpired = (token: string | null): boolean => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
};

/**
 * Formats token for logging or debugging
 * @param token JWT token to format
 * @returns Formatted token object or error message
 */
export const formatTokenForDisplay = (token: string | null): any => {
  if (!token) return { error: 'No token provided' };
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { error: 'Invalid token format (expected 3 parts)' };
    }
    
    return {
      header: JSON.parse(atob(parts[0])),
      payload: JSON.parse(atob(parts[1])),
      signature: parts[2].substring(0, 10) + '...' // Truncate for readability
    };
  } catch (e) {
    return { error: `Error formatting token: ${e}` };
  }
}; 